<template>
  <CContainer>
    <CRow>
      <CCol>
        <Label required> Start Date </Label>
        <CustomDatePicker
          v-model="state.form.from_date"
          :masks="{ L: 'MM/DD/YYYY' }"
          :invalid-feedback="errors.from_date"
          class="cs-date-picker"
          :popover="{ visibility: 'click' }"
          :min-date="minFromDate"
          :max-date="maxDate"
          @update:model-value="onStartDateChange"
        >
        </CustomDatePicker>
      </CCol>
      <CCol>
        <Label required> End Date </Label>

        <CustomDatePicker
          v-model="state.form.to_date"
          :masks="{ L: 'MM/DD/YYYY' }"
          :invalid-feedback="errors.to_date"
          class="cs-date-picker"
          :popover="{ visibility: 'click' }"
          :min-date="minToDate"
          :max-date="maxDate"
        >
        </CustomDatePicker>
      </CCol>
    </CRow>
    <CRow class="mt-3">
      <CCol class="text-end">
        <LoadingButton class="me-2" rounded @click="handleClose"
          >Cancel</LoadingButton
        >
        <LoadingButton
          solid
          rounded
          @click="handleSubmit"
          :is-loading="state.isLoading"
          >Submit</LoadingButton
        >
      </CCol>
    </CRow>
    <CAlert
      v-if="state.reqResponse.message"
      class="mt-4"
      :color="state.reqResponse.type"
      :show="!!state.reqResponse.message"
      close-button
    >
      {{ state.reqResponse.message }}
    </CAlert>
  </CContainer>
</template>

<script>
import { onMounted, computed } from "vue"
import { helpers, required } from "@vuelidate/validators"
import Label from "@/v3components/shared/Form/Label.vue"
import LoadingButton from "@/v3components/shared/Buttons/LoadingButton.vue"
import CustomDatePicker from "@/v3components/shared/Form/CustomDatePicker.vue"
import useVuelidate from "@vuelidate/core"
import moment from "moment-timezone"
import helpersJS from "../../../helpers/index.js"
import { inject, reactive } from "vue"
import { useStore } from "vuex"

export default {
  name: "CopyToCurrentSchoolYearForm",
  components: {
    Label,
    LoadingButton,
    CustomDatePicker
  },
  props: {
    startDate: {
      type: String,
      default: null
    },
    endDate: {
      type: String,
      default: null
    },
    selectedItems: {
      type: Array,
      default: () => []
    },
    type: {
      type: String,
      default: "room_restriction"
    }
  },
  emits: ["close", "onModalSubmit"],
  setup(props, { emit }) {
    const store = useStore()
    const modal = inject("modal")
    const validationMessages = {
      required: "This field is required"
    }
    const state = reactive({
      isLoading: false,
      reqResponse: {
        type: "",
        message: "",
        errors: {}
      },
      form: {
        from_date: moment().format("MMM DD YYYY"),
        to_date: moment().format("MMM DD YYYY")
      }
    })
    const activeSchoolTimeZone = computed(() => {
      return store.getters["schools/activeSchoolTimeZone"]
    })
    const initializeDates = () => {
      const today = moment()
        .tz(activeSchoolTimeZone.value)
        .format("MMM DD YYYY HH:mm:ss")
      state.form.from_date = today
      state.form.to_date = maxDate.value
    }
    const minFromDate = computed(() => {
      return moment().format("MMM DD YYYY")
    })

    const maxDate = computed(() => {
      return helpersJS.maxSchoolYearDate()
    })

    const minToDate = computed(() => {
      return state.form.from_date
        ? state.form.from_date
        : moment().format("MMM DD YYYY")
    })
    const handleSubmit = () => {
      if (!isFormValid.value) {
        v$.value.$touch()
        return
      }
      if (props.type === "room_restriction") {
        handleRoomRestrictionCopy()
      } else if (props.type === "pass_block") {
        handlePassBlockCopy()
      } else if (props.type === "student_pass_limit") {
        handleStudentPassLimitCopy()
      }
    }
    const handleRoomRestrictionCopy = () => {
      state.isLoading = true
      const data = {
        room_restriction_ids: props.selectedItems,
        from_date: helpersJS.currTzDate(state.form.from_date),
        to_date: helpersJS.currTzDate(state.form.to_date)
      }
      store
        .dispatch("rooms/bulkCopy", data)
        .then((response) => {
          state.isLoading = false
          modal.setAction("bulkCopySuccess", { data: response.data })
          setTimeout(() => {
            modal.close()
          }, 1000)
        })
        .catch((err) => {
          modal.setAction("bulkCopyError")
          const response = err.response.data
          setErrorResponse(response.message, response.errors)
          state.isLoading = false
          modal.close()
        })
    }
    const handlePassBlockCopy = () => {
      state.isLoading = true
      const data = {
        pass_block_ids: props.selectedItems,
        from_date: helpersJS.currTzDate(state.form.from_date),
        to_date: helpersJS.currTzDate(state.form.to_date)
      }
      store
        .dispatch("passBlocks/bulkCopyPassBlocks", data)
        .then((response) => {
          state.isLoading = false
          modal.setAction("bulkCopySuccess", { data: response.data })
          setTimeout(() => {
            modal.close()
          }, 1000)
        })
        .catch((err) => {
          modal.setAction("bulkCopyError")
          const response = err.response.data
          setErrorResponse(response.message, response.errors)
          state.isLoading = false
          modal.close()
        })
    }
    const handleStudentPassLimitCopy = () => {
      state.isLoading = true
      const data = {
        pass_limit_ids: props.selectedItems,
        from_date: helpersJS.currTzDate(state.form.from_date),
        to_date: helpersJS.currTzDate(state.form.to_date)
      }
      store
        .dispatch("passLimits/bulkCopyStudentPassLimits", data)
        .then((response) => {
          state.isLoading = false
          modal.setAction("bulkCopySuccess", { data: response.data })
          setTimeout(() => {
            modal.close()
          }, 1000)
        })
        .catch((err) => {
          modal.setAction("bulkCopyError")
          const response = err.response.data
          setErrorResponse(response.message, response.errors)
          state.isLoading = false
          modal.close()
        })
    }
    const setErrorResponse = (message, errors) => {
      state.reqResponse = {
        type: "danger",
        message: message ? message : "Someting went wrong!",
        errors: errors ? errors : {}
      }
    }

    const handleClose = () => {
      modal.setAction("bulkCopyCancel")
      modal.close()
    }

    const validations = {
      form: {
        from_date: {
          required: helpers.withMessage(validationMessages.required, required)
        },
        to_date: {
          required: helpers.withMessage(validationMessages.required, required)
        }
      }
    }

    const v$ = useVuelidate(validations.form, state.form)
    const isFormValid = computed(() => !v$.value.$invalid)

    const errors = computed(() => {
      const errorObj = {}
      v$.value.$errors.forEach((err) => {
        errorObj[err.$property] = err.$message
      })

      return errorObj
    })

    onMounted(() => {
      initializeDates()
    })
    const onStartDateChange = () => {
      state.form.to_date =
        state.form.to_date && state.form.to_date < state.form.from_date
          ? state.form.from_date
          : state.form.to_date
    }

    return {
      props,
      emit,
      handleSubmit,
      handleClose,
      onStartDateChange,
      errors,
      minFromDate,
      maxDate,
      minToDate,
      state
    }
  }
}
</script>
